// Admin → Family detail. The operator's single drill-in for one family:
// everyone in it, what they are on, what they have paid, and how the kids are
// doing. Track U.
//
// Before this it was a 78-line read-only stub (parent, kid names, subscription
// badge) and every real question — "change their email", "give them a month",
// "what have they paid us", "is the child actually using it" — sent the
// operator to a different page or to the database. Four tabs, one page.
//
// 🔴 THREE INVARIANTS THIS PAGE MUST KEEP:
//
//  1. PROGRESS IS READ-ONLY. The learning engine is frozen. The Progress tab
//     renders GET-only data and offers no edit affordance anywhere. Scoring, XP
//     and streaks belong to the engine alone.
//
//  2. NO BLENDED MONEY TOTAL. A family can hold invoices in several currencies
//     and there is no FX table in this system. Totals are per-currency; see
//     FamilyInvoices.
//
//  3. ONE GRANT PATH. Assigning a plan posts to the same
//     `POST /admin/subscriptions/grant` the Subscriptions page uses. A comp is
//     an ORDINARY active subscription — it entitles because it is active, never
//     because something checks `metadata.comp`.
//
// Admin chrome is English-only and always LTR, so there are no translation keys
// here by design.

import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useParams } from '@tanstack/react-router';
import { useState } from 'react';

import {
  DetailRow,
  DetailSection,
  EmptyState,
  ErrorState,
  Icon,
  PageHeader,
  StatusBadge,
  StatTile,
  StatGrid,
} from '~/components';
import { AssignPlanDialog } from '~/components/family/AssignPlanDialog';
import { FamilyInvoices } from '~/components/family/FamilyInvoices';
import { KidProgressCard } from '~/components/family/KidProgressCard';
import { UserEditDialog } from '~/components/family/UserEditDialog';
import { getFamily, getFamilyProgress, getUser, listFamilyInvoices } from '~/lib/admin';

export const Route = createFileRoute('/_authed/families/$id')({
  component: FamilyDetailPage,
});

type TabId = 'overview' | 'invoices' | 'progress';

const TABS: ReadonlyArray<{ id: TabId; label: string; icon: 'family' | 'receipt' | 'chart' }> = [
  { id: 'overview', label: 'Overview', icon: 'family' },
  { id: 'invoices', label: 'Invoices', icon: 'receipt' },
  { id: 'progress', label: 'Progress', icon: 'chart' },
];

function FamilyDetailPage() {
  const { id } = useParams({ from: '/_authed/families/$id' });
  const [tab, setTab] = useState<TabId>('overview');
  const [assignOpen, setAssignOpen] = useState(false);
  // Which user the edit dialog is for. `null` closes it. Holding the id (not
  // the row) means the dialog always edits server truth, never a stale copy.
  const [editUserId, setEditUserId] = useState<string | null>(null);

  const family = useQuery({
    queryKey: ['family', id],
    queryFn: ({ signal }) => getFamily(id, signal),
  });

  // Both secondary datasets are fetched only when their tab is opened. The
  // progress one especially: it is a child's record, and an audit-logged read
  // should happen because someone asked to see it, not because a page loaded.
  const invoices = useQuery({
    queryKey: ['family-invoices', id],
    queryFn: ({ signal }) => listFamilyInvoices(id, signal),
    enabled: tab === 'invoices',
  });

  const progress = useQuery({
    queryKey: ['family-progress', id],
    queryFn: ({ signal }) => getFamilyProgress(id, signal),
    enabled: tab === 'progress',
  });

  // The full user row for the edit dialog — the family payload carries only a
  // label, and editing needs country/phone/locale/verification state too.
  const editUser = useQuery({
    queryKey: ['user', editUserId],
    queryFn: ({ signal }) => getUser(editUserId!, signal),
    enabled: editUserId != null,
  });

  const f = family.data;

  if (family.isLoading) {
    return (
      <div className="mx-auto max-w-5xl space-y-4">
        <div className="h-24 animate-pulse rounded-2xl bg-admin-100" />
        <div className="h-64 animate-pulse rounded-2xl bg-admin-100" />
      </div>
    );
  }

  if (family.error || !f) {
    return (
      <div className="mx-auto max-w-5xl">
        <BackLink />
        <ErrorState
          error={family.error ?? new Error('Family not found')}
          onRetry={() => void family.refetch()}
          className="mt-4"
        />
      </div>
    );
  }

  const parentLabel = f.parent?.label ?? 'Family';

  return (
    <div className="mx-auto max-w-5xl">
      <BackLink />

      <PageHeader
        className="mt-2"
        title={parentLabel}
        description={`Joined ${formatDate(f.createdAt)} · ${f.kids.length} kid${f.kids.length === 1 ? '' : 's'}`}
        badge={
          <div className="flex flex-wrap items-center gap-1.5">
            <StatusBadge status={f.status} dot />
            {f.subscription ? (
              <StatusBadge status={f.subscription.status} dot />
            ) : (
              <StatusBadge status="none" label="No subscription" tone="neutral" />
            )}
          </div>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {f.parent && (
              <button
                type="button"
                onClick={() => setEditUserId(f.parent!.id)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-admin-200 px-3.5 py-2 text-sm font-semibold text-admin-700 transition-colors hover:bg-admin-50"
              >
                <Icon name="edit" className="h-4 w-4" />
                Edit parent
              </button>
            )}
            <button
              type="button"
              onClick={() => setAssignOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700"
            >
              <Icon name="card" className="h-4 w-4" />
              Assign a plan
            </button>
          </div>
        }
      />

      <Tabs value={tab} onChange={setTab} />

      {tab === 'overview' && <OverviewTab family={f} onEditUser={setEditUserId} />}

      {tab === 'invoices' && (
        <div className="mt-4">
          <FamilyInvoices
            invoices={invoices.data ?? []}
            loading={invoices.isLoading}
            error={invoices.error}
            onRetry={() => void invoices.refetch()}
          />
        </div>
      )}

      {tab === 'progress' && (
        <ProgressTab
          kids={progress.data ?? []}
          loading={progress.isLoading}
          error={progress.error}
          onRetry={() => void progress.refetch()}
        />
      )}

      <AssignPlanDialog
        open={assignOpen}
        onOpenChange={setAssignOpen}
        familyId={f.id}
        familyLabel={parentLabel}
        currentStatus={f.subscription?.status ?? null}
      />

      <UserEditDialog
        open={editUserId != null}
        onOpenChange={(open) => !open && setEditUserId(null)}
        user={editUser.data ?? null}
        // The family payload embeds labels and statuses for parent and kids, so
        // it has to be refetched after an edit or the page shows the old name.
        invalidateKeys={[['family', id], ['families']]}
      />
    </div>
  );
}

// ─── Overview ─────────────────────────────────────────────────────────────────

function OverviewTab({
  family,
  onEditUser,
}: {
  family: NonNullable<Awaited<ReturnType<typeof getFamily>>>;
  onEditUser: (id: string) => void;
}) {
  const sub = family.subscription;

  return (
    <div className="mt-4 space-y-4">
      <section className="rounded-2xl border border-admin-200 bg-white p-5">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-admin-900">Parent</h2>
        </div>
        {family.parent ? (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <Link
                to="/users/$id"
                params={{ id: family.parent.id }}
                className="font-medium text-brand-600 hover:underline"
              >
                {family.parent.label}
              </Link>
              <p className="truncate text-sm text-admin-500">{family.parent.email ?? '—'}</p>
            </div>
            <button
              type="button"
              onClick={() => onEditUser(family.parent!.id)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-admin-200 px-3 py-1.5 text-sm font-medium text-admin-700 transition-colors hover:bg-admin-50"
            >
              <Icon name="edit" className="h-3.5 w-3.5" />
              Edit
            </button>
          </div>
        ) : (
          <p className="mt-2 text-sm text-admin-400">No parent on this family.</p>
        )}
      </section>

      <section className="rounded-2xl border border-admin-200 bg-white p-5">
        <h2 className="text-sm font-semibold text-admin-900">Kids ({family.kids.length})</h2>
        {family.kids.length === 0 ? (
          <p className="mt-2 text-sm text-admin-400">No kids on this family yet.</p>
        ) : (
          <ul className="mt-3 divide-y divide-admin-100">
            {family.kids.map((k) => (
              <li key={k.id} className="flex flex-wrap items-center gap-3 py-2.5">
                <Link
                  to="/users/$id"
                  params={{ id: k.id }}
                  className="min-w-0 flex-1 truncate font-medium text-brand-600 hover:underline"
                >
                  {k.label}
                </Link>
                {k.username && <span className="text-sm text-admin-400">@{k.username}</span>}
                <StatusBadge status={k.status} dot />
                <button
                  type="button"
                  onClick={() => onEditUser(k.id)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-admin-200 px-3 py-1.5 text-sm font-medium text-admin-700 transition-colors hover:bg-admin-50"
                >
                  <Icon name="edit" className="h-3.5 w-3.5" />
                  Edit
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-2xl border border-admin-200 bg-white p-5">
        <h2 className="text-sm font-semibold text-admin-900">Subscription</h2>
        {!sub ? (
          <p className="mt-2 text-sm text-admin-400">
            No subscription. Use <span className="font-medium">Assign a plan</span> to grant access.
          </p>
        ) : (
          <dl className="mt-2">
            <DetailSection>
              <DetailRow label="Status">
                <StatusBadge status={sub.status} dot />
              </DetailRow>
              {/* A raw translation key: the admin has no i18n runtime (chrome is
                  English-only), and the key is the stable identifier a content
                  editor can actually search for. */}
              <DetailRow label="Plan">
                <code className="text-xs">{sub.planNameKey || '—'}</code>
              </DetailRow>
              <DetailRow label="Billing">
                {sub.currency} · {sub.interval === 'year' ? 'Yearly' : 'Monthly'}
              </DetailRow>
              <DetailRow label="Renews">
                {sub.currentPeriodEnd ? formatDate(sub.currentPeriodEnd) : '—'}
              </DetailRow>
            </DetailSection>
          </dl>
        )}
      </section>
    </div>
  );
}

// ─── Progress ─────────────────────────────────────────────────────────────────

function ProgressTab({
  kids,
  loading,
  error,
  onRetry,
}: {
  kids: Awaited<ReturnType<typeof getFamilyProgress>>;
  loading: boolean;
  error: unknown;
  onRetry: () => void;
}) {
  if (loading) {
    return (
      <div className="mt-4 space-y-4">
        <div className="h-56 animate-pulse rounded-2xl bg-admin-100" />
      </div>
    );
  }
  if (error) {
    return <ErrorState error={error} onRetry={onRetry} className="mt-4" />;
  }
  if (kids.length === 0) {
    return (
      <EmptyState
        className="mt-4"
        icon="chart"
        title="No learners on this family"
        description="Progress appears here once the parent adds a kid."
      />
    );
  }

  // Roll-up across the kids. Counts only — these are all plain integers of the
  // same unit, so there is no currency-style blending hazard here.
  const totalLessons = kids.reduce((n, k) => n + k.lessonsCompleted, 0);
  const totalXp = kids.reduce((n, k) => n + k.xpTotal, 0);
  const bestStreak = kids.reduce((n, k) => Math.max(n, k.streakCurrent), 0);

  return (
    <div className="mt-4 space-y-4">
      <StatGrid columns={3}>
        <StatTile label="Lessons completed" value={totalLessons} icon="book" hint="whole family" />
        <StatTile label="Total XP" value={totalXp.toLocaleString('en-US')} icon="sparkle" />
        <StatTile
          label="Best current streak"
          value={`${bestStreak} day${bestStreak === 1 ? '' : 's'}`}
          icon="trendUp"
        />
      </StatGrid>

      {/* 🔴 Read-only. A child's learning record is viewed here, never edited —
          the learning engine owns every one of these numbers. */}
      {kids.map((kid) => (
        <KidProgressCard key={kid.userId} kid={kid} />
      ))}
    </div>
  );
}

// ─── chrome ───────────────────────────────────────────────────────────────────

function Tabs({ value, onChange }: { value: TabId; onChange: (t: TabId) => void }) {
  return (
    <div
      role="tablist"
      aria-label="Family sections"
      className="flex gap-1 border-b border-admin-200"
    >
      {TABS.map((t) => {
        const active = t.id === value;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.id)}
            className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-3.5 py-2.5 text-sm font-medium transition-colors ${
              active
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-admin-500 hover:text-admin-800'
            }`}
          >
            <Icon name={t.icon} className="h-4 w-4" />
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

function BackLink() {
  return (
    <Link to="/families" className="text-sm text-brand-600 hover:underline">
      ← Families
    </Link>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}
